import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { apiGet, apiPatch, apiDelete, ApiClientError } from '@client/lib/api';
import { formatCents, parseCents } from '@shared/money';
import { RECURRENCE_TYPES } from '@shared/constants';
import type { RecurringStatus } from '@shared/constants';
import styles from './RecurringDetailPage.module.css';

/** A recurring service rule as returned by GET /api/v1/recurring/:id. */
interface RecurringRule {
  id: string;
  organizationId: string;
  customerId: string;
  propertyId: string;
  serviceId: string | null;
  name: string;
  status: string;
  startDate: string;
  endDate: string | null;
  recurrenceType: string;
  intervalCount: number;
  dayOfWeek: number | null;
  priceCents: number;
  estimatedDurationMinutes: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  companyName: string | null;
}

interface Property {
  id: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  postalCode: string;
}

/** dayOfWeek is 0=Sunday through 6=Saturday. */
const DAY_NAMES: readonly string[] = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'not-found' }
  | {
      kind: 'loaded';
      rule: RecurringRule;
      customerName: string;
      propertyName: string;
    };

/**
 * Normalize a nullable field for comparison: '' and null are the SAME state.
 * Comparing '' !== null makes an untouched field look edited on every save.
 */
function normalizeNullable(value: string | null | undefined): string | null {
  if (value === undefined || value === '') return null;
  return value;
}

/**
 * The recurring service detail screen.
 *
 * Shows one recurring service rule with its frequency, price, dates, day of
 * week, notes, and the customer and property names (fetched separately from
 * their own endpoints). Edits are saved with PATCH, sending ONLY the fields
 * that changed — a diff against what was loaded. Pause/Resume toggles the
 * status. Stop (DELETE) is behind a confirm and guarded against double-tap
 * with a ref.
 */
export function RecurringDetailPage() {
  const { id } = useParams<{ id: string }>();
  const ruleId = id ?? '';

  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  /** The working copy of the rule as the owner edits it. */
  const [draft, setDraft] = useState<RecurringRule | null>(null);
  /** The original rule as loaded, for diffing. */
  const originalRule = useRef<RecurringRule | null>(null);

  /** Save error message, or null. */
  const [saveError, setSaveError] = useState<string | null>(null);
  /** Save is in flight. */
  const [saving, setSaving] = useState(false);

  /** The stop-confirm dialog is open. */
  const [confirmingStop, setConfirmingStop] = useState(false);
  /** A stop is in flight. */
  const [stopping, setStopping] = useState(false);
  /** The ref is the exactly-once guard for Stop. */
  const stoppingRef = useRef(false);

  const load = useCallback(async () => {
    if (!ruleId) return;
    setState({ kind: 'loading' });
    setSaveError(null);
    try {
      const rule = await apiGet<RecurringRule>(`/api/v1/recurring/${ruleId}`);

      // Fetch customer and property names separately — the detail response
      // carries only the ids.
      const [customers, properties] = await Promise.all([
        apiGet<Customer[]>('/api/v1/customers'),
        apiGet<Property[]>('/api/v1/properties'),
      ]);

      const customer = customers.find((c) => c.id === rule.customerId);
      const customerName =
        customer?.companyName ??
        `${customer?.firstName ?? ''} ${customer?.lastName ?? ''}`.trim() ??
        'Unknown customer';

      const property = properties.find((p) => p.id === rule.propertyId);
      const propertyName = property
        ? [
            property.addressLine1,
            property.addressLine2,
            property.city,
            property.state,
            property.postalCode,
          ]
            .filter((part) => part !== null && part !== '')
            .join(', ')
        : 'Unknown property';

      setState({ kind: 'loaded', rule, customerName, propertyName });
      setDraft(rule);
      originalRule.current = rule;
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 404) {
        setState({ kind: 'not-found' });
      } else {
        setState({
          kind: 'error',
          message:
            err instanceof ApiClientError ? err.message : 'Could not load the recurring service.',
        });
      }
    }
  }, [ruleId]);

  useEffect(() => {
    void load();
  }, [load]);

  function buildPatchBody(draft: RecurringRule): Record<string, unknown> {
    const body: Record<string, unknown> = {};
    const orig = originalRule.current;
    if (!orig) return body;

    if (draft.name !== orig.name) {
      body.name = draft.name;
    }

    if (draft.startDate !== orig.startDate) {
      body.startDate = draft.startDate;
    }

    // endDate: '' and null are the same state.
    if (normalizeNullable(draft.endDate) !== normalizeNullable(orig.endDate)) {
      body.endDate = normalizeNullable(draft.endDate);
    }

    if (draft.recurrenceType !== orig.recurrenceType) {
      body.recurrenceType = draft.recurrenceType;
    }

    if (draft.intervalCount !== orig.intervalCount) {
      body.intervalCount = draft.intervalCount;
    }

    if (showDayPicker) {
      const draftDay = draft.dayOfWeek ?? null;
      const origDay = orig.dayOfWeek ?? null;
      if (draftDay !== origDay) {
        body.dayOfWeek = draftDay;
      }
    }

    if (draft.priceCents !== orig.priceCents) {
      body.priceCents = draft.priceCents;
    }

    if (draft.estimatedDurationMinutes !== orig.estimatedDurationMinutes) {
      body.estimatedDurationMinutes = draft.estimatedDurationMinutes;
    }

    // notes: '' and null are the same state.
    if (normalizeNullable(draft.notes) !== normalizeNullable(orig.notes)) {
      body.notes = normalizeNullable(draft.notes);
    }

    return body;
  }

  const handleSave = useCallback(async () => {
    // Guarded here rather than by an early return above: these hooks now run
    // on EVERY render, including the loading one, so they cannot rely on
    // narrowing that only holds once the rule has loaded.
    if (!draft) return;
    const body = buildPatchBody(draft);
    if (Object.keys(body).length === 0) {
      setSaveError(null);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const updated = await apiPatch<RecurringRule>(`/api/v1/recurring/${ruleId}`, body);
      setDraft(updated);
      originalRule.current = updated;
    } catch (err) {
      setSaveError(err instanceof ApiClientError ? err.message : 'Could not save changes.');
    } finally {
      setSaving(false);
    }
    // `draft` MUST be a dependency: without it handleSave captures the draft
    // from the first render, the diff against the original is always empty,
    // and Save silently does nothing.
  }, [ruleId, draft]);

  const handlePauseResume = useCallback(async () => {
    if (!draft) return;
    // Derived from draft inside the handler: `canPause` is computed after the
    // early returns, which these hooks now run before.
    const newStatus: RecurringStatus = draft.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await apiPatch<RecurringRule>(`/api/v1/recurring/${ruleId}`, {
        status: newStatus,
      });
      setDraft(updated);
      originalRule.current = updated;
    } catch (err) {
      setSaveError(err instanceof ApiClientError ? err.message : 'Could not update the status.');
    } finally {
      setSaving(false);
    }
  }, [ruleId, draft]);

  const handleStop = useCallback(async () => {
    // The ref is the exactly-once guard. Two clicks in one tick both call
    // this handler before React re-renders; the first sets the ref to true
    // synchronously, so the second returns immediately.
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    setStopping(true);
    try {
      await apiDelete(`/api/v1/recurring/${ruleId}`);
      // The rule is now ENDED. Navigate back to the list.
      window.location.href = '/recurring';
    } catch (err) {
      setSaveError(
        err instanceof ApiClientError ? err.message : 'Could not stop the recurring service.'
      );
      setStopping(false);
      stoppingRef.current = false;
    }
  }, [ruleId]);

  if (state.kind === 'loading') {
    return (
      <main id="main" className={styles.container}>
        <p className={styles.status} aria-live="polite">
          Loading recurring service…
        </p>
      </main>
    );
  }

  if (state.kind === 'error') {
    return (
      <main id="main" className={styles.container}>
        <p className={styles.error} role="alert">
          {state.message}
        </p>
        <button type="button" className={styles.retryButton} onClick={() => void load()}>
          Retry
        </button>
      </main>
    );
  }

  if (state.kind === 'not-found') {
    return (
      <main id="main" className={styles.container}>
        <h1 className={styles.title}>Not found</h1>
        <p className={styles.status}>No recurring service matches that id.</p>
      </main>
    );
  }

  const { rule, customerName, propertyName } = state;

  if (!draft) {
    return null;
  }

  const showDayPicker = draft.recurrenceType === 'WEEKLY' || draft.recurrenceType === 'BIWEEKLY';

  const priceDisplay = formatCents(draft.priceCents);

  const canPause = draft.status === 'ACTIVE';
  const canResume = draft.status === 'PAUSED';

  /**
   * Build the PATCH body by diffing the draft against the original rule.
   * Only fields that changed are sent. '' and null are treated as the same
   * state for nullable fields (endDate, notes).
   */
  // `draft` is taken as a PARAMETER rather than closed over: the
  // `if (!draft) return null` guard above does not narrow inside a function
  // declaration, because TypeScript cannot know when the function is called.

  return (
    <main id="main" className={styles.container}>
      {/*
        The SAVED name, not the draft. Bound to the draft it re-titled the page
        on every keystroke, so the heading stopped identifying the record you
        opened and started mirroring an unsaved edit — and after a FAILED save
        it would keep showing a name the server never accepted.
      */}
      <h1 className={styles.title}>{rule.name}</h1>

      <section className={styles.detailGrid}>
        <div className={styles.detailRow}>
          <span className={styles.fieldLabel}>Customer</span>
          <span className={styles.fieldValue}>{customerName}</span>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.fieldLabel}>Property</span>
          <span className={styles.fieldValue}>{propertyName}</span>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.fieldLabel}>Status</span>
          <span className={styles.fieldValue}>{draft.status}</span>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.fieldLabel}>Recurrence type</span>
          <span className={styles.fieldValue}>{draft.recurrenceType}</span>
        </div>

        {showDayPicker && (
          <div className={styles.detailRow}>
            <span className={styles.fieldLabel}>Day of week</span>
            <span className={styles.fieldValue}>
              {draft.dayOfWeek !== null &&
              draft.dayOfWeek >= 0 &&
              draft.dayOfWeek < DAY_NAMES.length
                ? DAY_NAMES[draft.dayOfWeek]
                : '—'}
            </span>
          </div>
        )}

        <div className={styles.detailRow}>
          <span className={styles.fieldLabel}>Interval</span>
          <span className={styles.fieldValue}>{draft.intervalCount}</span>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.fieldLabel}>Price</span>
          <span className={styles.fieldValue}>{priceDisplay}</span>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.fieldLabel}>Start date</span>
          <span className={styles.fieldValue}>{draft.startDate}</span>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.fieldLabel}>End date</span>
          <span className={styles.fieldValue}>{draft.endDate ?? '—'}</span>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.fieldLabel}>Estimated duration (minutes)</span>
          <span className={styles.fieldValue}>{draft.estimatedDurationMinutes}</span>
        </div>
      </section>

      <section className={styles.editSection}>
        <h2 className={styles.editTitle}>Edit</h2>

        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
          noValidate
        >
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Name</span>
            <input
              className={styles.input}
              type="text"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Start date</span>
            <input
              className={styles.input}
              type="date"
              value={draft.startDate}
              onChange={(e) => setDraft({ ...draft, startDate: e.target.value })}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>End date</span>
            <input
              className={styles.input}
              type="date"
              value={draft.endDate ?? ''}
              onChange={(e) => setDraft({ ...draft, endDate: e.target.value || null })}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Recurrence type</span>
            <select
              className={styles.select}
              value={draft.recurrenceType}
              onChange={(e) => setDraft({ ...draft, recurrenceType: e.target.value })}
            >
              {RECURRENCE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>

          {showDayPicker && (
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Day of week</span>
              <select
                className={styles.select}
                value={draft.dayOfWeek === null ? '' : String(draft.dayOfWeek)}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    dayOfWeek: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              >
                <option value="">Select a day</option>
                {DAY_NAMES.map((day, i) => (
                  <option key={day} value={i}>
                    {day}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Interval</span>
            <input
              className={styles.input}
              type="number"
              min={1}
              value={draft.intervalCount}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  intervalCount: Number(e.target.value),
                })
              }
            />
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Price</span>
            <input
              className={styles.input}
              type="text"
              value={priceDisplay}
              onChange={(e) => {
                const cents = parseCents(e.target.value);
                setDraft({
                  ...draft,
                  priceCents: cents === null ? 0 : cents,
                });
              }}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Estimated duration (minutes)</span>
            <input
              className={styles.input}
              type="number"
              min={0}
              value={draft.estimatedDurationMinutes}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  estimatedDurationMinutes: Number(e.target.value),
                })
              }
            />
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Notes</span>
            <textarea
              className={styles.input}
              value={draft.notes ?? ''}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value || null })}
              placeholder="Optional notes"
            />
          </label>

          {saveError && (
            <p className={styles.error} role="alert">
              {saveError}
            </p>
          )}

          <button type="submit" className={styles.saveButton} disabled={saving} aria-busy={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </form>
      </section>

      <section className={styles.actionsSection}>
        {canPause && (
          <button
            type="button"
            className={styles.actionButton}
            onClick={() => void handlePauseResume()}
            disabled={saving}
          >
            Pause
          </button>
        )}
        {canResume && (
          <button
            type="button"
            className={styles.actionButton}
            onClick={() => void handlePauseResume()}
            disabled={saving}
          >
            Resume
          </button>
        )}
        <button
          type="button"
          className={styles.stopButton}
          onClick={() => setConfirmingStop(true)}
          disabled={stopping}
        >
          Stop
        </button>
      </section>

      {confirmingStop && (
        <div className={styles.overlay} role="dialog" aria-modal="true">
          <div className={styles.confirmDialog}>
            <h2 className={styles.confirmTitle}>Stop this recurring service?</h2>
            <p className={styles.confirmText}>
              This stops the rule from creating any future jobs. Jobs already generated from this
              service are not affected.
            </p>
            <div className={styles.confirmButtons}>
              <button
                type="button"
                className={styles.confirmButton}
                onClick={() => void handleStop()}
                disabled={stopping}
                aria-busy={stopping}
              >
                {stopping ? 'Stopping…' : 'Stop service'}
              </button>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => setConfirmingStop(false)}
                disabled={stopping}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
